import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClockIcon, ShieldCheckIcon, MessageSquareIcon, ChevronDownIcon } from 'lucide-react';
export function MarketAccessPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqs = [{
    q: 'What types of organizations do you typically work with?',
    a: 'We work with US-based corporations, NGOs, development finance institutions, and government agencies seeking to establish or expand operations in Nigeria and Ghana. Our clients range from Fortune 500 companies to mid-market firms entering West Africa for the first time.'
  }, {
    q: 'How long does a typical engagement last?',
    a: 'Engagement timelines vary based on scope. A focused policy analysis may take 4–6 weeks, while a comprehensive market entry strategy typically spans 3–6 months. We also offer ongoing retainer arrangements for continuous advisory support.'
  }, {
    q: 'Do you have staff on the ground in Nigeria and Ghana?',
    a: 'Yes. We maintain operational hubs in Victoria Island, Lagos and Cantonments, Accra with local analysts, researchers, and partner networks. This on-the-ground presence is central to the quality and timeliness of our intelligence.'
  }, {
    q: 'What makes Emma Kay Consulting different from larger firms?',
    a: 'Our exclusive focus on Nigeria and Ghana gives us unmatched depth of expertise. Unlike generalist firms, every member of our team has direct, lived experience in these markets. We offer the rigor of a large consultancy with the agility and personal attention of a boutique firm.'
  }, {
    q: 'Is the initial consultation free?',
    a: 'We offer a complimentary 30-minute introductory call to understand your needs and determine if there is a mutual fit. Following this, we provide a detailed scope of work and fee proposal before any engagement begins.'
  }];
  return <div className="w-full pt-20 lg:pt-24" data-id="element-340">
      {/* Page Header */}
      <div className="bg-navy py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-341">
        <div className="max-w-7xl mx-auto" data-id="element-342">
          <div className="font-sans text-sm font-semibold text-white/50 uppercase tracking-wider mb-4" data-id="element-343">
            <Link to="/" className="hover:text-gold transition-colors" data-id="element-344">
              Home
            </Link>
            <span className="mx-2 text-gold/50" data-id="element-345">/</span>
            <span className="text-gold" data-id="element-346">Market Access Inquiry</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-bold" data-id="element-347">
            Market Access Inquiry
          </h1>
          <div className="h-1 w-24 bg-gold mt-6" data-id="element-348"></div>
        </div>
      </div>

      {/* What to Expect */}
      <section className="py-16 bg-cream-dark px-4 sm:px-6 lg:px-8" data-id="element-349">
        <div className="max-w-5xl mx-auto" data-id="element-350">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-351">
            <div className="flex flex-col items-center" data-id="element-352">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-4" data-id="element-353">
                <ClockIcon className="h-6 w-6 text-navy" data-id="element-354" />
              </div>
              <h3 className="font-sans font-bold text-sm text-navy uppercase tracking-wider mb-2" data-id="element-355">
                Response Time
              </h3>
              <p className="font-sans text-sm text-text-muted" data-id="element-356">
                We respond to all inquiries within 24 business hours
              </p>
            </div>
            <div className="flex flex-col items-center" data-id="element-357">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-4" data-id="element-358">
                <ShieldCheckIcon className="h-6 w-6 text-navy" data-id="element-359" />
              </div>
              <h3 className="font-sans font-bold text-sm text-navy uppercase tracking-wider mb-2" data-id="element-360">
                Confidential
              </h3>
              <p className="font-sans text-sm text-text-muted" data-id="element-361">
                All communications are held in strict confidence
              </p>
            </div>
            <div className="flex flex-col items-center" data-id="element-362">
              <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center mb-4" data-id="element-363">
                <MessageSquareIcon className="h-6 w-6 text-navy" data-id="element-364" />
              </div>
              <h3 className="font-sans font-bold text-sm text-navy uppercase tracking-wider mb-2" data-id="element-365">
                No Obligation
              </h3>
              <p className="font-sans text-sm text-text-muted" data-id="element-366">
                Initial consultation is complimentary with no commitment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Contact */}
      <section className="py-20 bg-cream px-4 sm:px-6 lg:px-8" data-id="element-367">
        <div className="max-w-7xl mx-auto" data-id="element-368">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-369">
            {/* Left: Contact Form */}
            <div className="bg-white rounded-card shadow-sm border border-cream-dark p-8 md:p-10" data-id="element-370">
              <h2 className="font-serif text-2xl text-navy font-bold mb-2" data-id="element-371">
                Request a Consultation
              </h2>
              <p className="font-sans text-sm text-text-muted mb-8" data-id="element-372">
                Fill out the form below and a member of our advisory team will
                be in touch.
              </p>
              <form className="space-y-6" onSubmit={e => e.preventDefault()} data-id="element-373">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-374">
                  <div data-id="element-375">
                    <label htmlFor="fullName" className="block font-sans text-sm font-semibold text-navy mb-2" data-id="element-376">
                      Full Name
                    </label>
                    <input type="text" id="fullName" className="w-full bg-cream border border-gray-200 rounded-card px-4 py-3 font-sans focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-colors" placeholder="Jane Doe" data-id="element-377" />
                  </div>
                  <div data-id="element-378">
                    <label htmlFor="organization" className="block font-sans text-sm font-semibold text-navy mb-2" data-id="element-379">
                      Organization
                    </label>
                    <input type="text" id="organization" className="w-full bg-cream border border-gray-200 rounded-card px-4 py-3 font-sans focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-colors" placeholder="Company Name" data-id="element-380" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-381">
                  <div data-id="element-382">
                    <label htmlFor="email" className="block font-sans text-sm font-semibold text-navy mb-2" data-id="element-383">
                      Email Address
                    </label>
                    <input type="email" id="email" className="w-full bg-cream border border-gray-200 rounded-card px-4 py-3 font-sans focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-colors" placeholder="jane@example.com" data-id="element-384" />
                  </div>
                  <div data-id="element-385">
                    <label htmlFor="phone" className="block font-sans text-sm font-semibold text-navy mb-2" data-id="element-386">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" className="w-full bg-cream border border-gray-200 rounded-card px-4 py-3 font-sans focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-colors" placeholder="+1 (555) 000-0000" data-id="element-387" />
                  </div>
                </div>

                <div data-id="element-388">
                  <label htmlFor="interest" className="block font-sans text-sm font-semibold text-navy mb-2" data-id="element-389">
                    Area of Interest
                  </label>
                  <select id="interest" className="w-full bg-cream border border-gray-200 rounded-card px-4 py-3 font-sans focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-colors appearance-none" data-id="element-390">
                    <option value="" data-id="element-391">Select an area of interest...</option>
                    <option value="policy" data-id="element-392">
                      Public Policy & Legislative Analysis
                    </option>
                    <option value="content" data-id="element-393">
                      Content Development & Strategy
                    </option>
                    <option value="me" data-id="element-394">Monitoring & Evaluation</option>
                    <option value="entry" data-id="element-395">Market Entry Strategy</option>
                    <option value="other" data-id="element-396">Other</option>
                  </select>
                </div>

                <div data-id="element-397">
                  <label htmlFor="message" className="block font-sans text-sm font-semibold text-navy mb-2" data-id="element-398">
                    Tell us about your objectives
                  </label>
                  <textarea id="message" rows={5} className="w-full bg-cream border border-gray-200 rounded-card px-4 py-3 font-sans focus:outline-none focus:ring-2 focus:ring-gold focus:border-gold transition-colors resize-none" placeholder="Please provide a brief overview of your goals in the region..." data-id="element-399"></textarea>
                </div>

                <button type="submit" className="w-full bg-gold hover:bg-gold-light text-navy font-sans font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-card transition-colors shadow-sm" data-id="element-400">
                  Submit Inquiry
                </button>
              </form>
            </div>

            {/* Right: Contact Info */}
            <div className="flex flex-col justify-start" data-id="element-401">
              <h2 className="font-serif text-3xl md:text-4xl text-navy font-bold mb-8" data-id="element-402">
                Get in Touch
              </h2>

              <div className="space-y-10 mb-12" data-id="element-403">
                <div data-id="element-404">
                  <h3 className="font-serif text-xl text-navy font-semibold mb-4 flex items-center" data-id="element-405">
                    <span className="w-8 h-px bg-gold mr-4" data-id="element-406"></span>
                    Washington D.C. Headquarters
                  </h3>
                  <address className="not-italic font-sans text-text-muted leading-relaxed pl-12" data-id="element-407">
                    <p data-id="element-408">1001 Pennsylvania Ave NW</p>
                    <p data-id="element-409">Suite 800 South</p>
                    <p data-id="element-410">Washington, DC 20004</p>
                    <p className="pt-2" data-id="element-411">T: +1 (202) 555-0198</p>
                    <p data-id="element-412">E: advisory@emmakayconsulting.com</p>
                  </address>
                </div>

                <div data-id="element-413">
                  <h3 className="font-serif text-xl text-navy font-semibold mb-4 flex items-center" data-id="element-414">
                    <span className="w-8 h-px bg-gold mr-4" data-id="element-415"></span>
                    West Africa Operations
                  </h3>
                  <div className="font-sans text-text-muted leading-relaxed pl-12" data-id="element-416">
                    <p className="mb-2" data-id="element-417">
                      Operating from strategic hubs in Victoria Island, Lagos
                      and Cantonments, Accra.
                    </p>
                    <p data-id="element-418">T: +234 (0) 1 454 0000</p>
                    <p data-id="element-419">E: westafrica@emmakayconsulting.com</p>
                  </div>
                </div>
              </div>

              <div className="bg-navy/5 p-6 rounded-card border-l-4 border-gold" data-id="element-420">
                <p className="font-sans text-sm text-navy font-semibold" data-id="element-421">
                  We typically respond to all inquiries within 24 business
                  hours. All communications are held in strict confidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-cream-dark px-4 sm:px-6 lg:px-8" data-id="element-422">
        <div className="max-w-3xl mx-auto" data-id="element-423">
          <div className="text-center mb-16" data-id="element-424">
            <span className="inline-block font-sans font-bold text-xs uppercase tracking-widest text-gold mb-4" data-id="element-425">
              Common Questions
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-navy font-bold mb-6" data-id="element-426">
              Frequently Asked Questions
            </h2>
            <div className="h-1 w-24 bg-gold mx-auto" data-id="element-427"></div>
          </div>

          <div className="space-y-3" data-id="element-428">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-card border border-gray-100 overflow-hidden" data-id="element-429">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full flex items-center justify-between p-6 text-left" data-id="element-430">
                  <span className="font-sans font-semibold text-navy pr-4" data-id="element-431">
                    {faq.q}
                  </span>
                  <motion.div animate={{
                rotate: openFaq === index ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="flex-shrink-0" data-id="element-432">
                    <ChevronDownIcon className="h-5 w-5 text-gold" data-id="element-433" />
                  </motion.div>
                </button>
                <motion.div initial={false} animate={{
              height: openFaq === index ? 'auto' : 0,
              opacity: openFaq === index ? 1 : 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-434">
                  <div className="px-6 pb-6" data-id="element-435">
                    <p className="font-sans text-text-muted leading-relaxed" data-id="element-436">
                      {faq.a}
                    </p>
                  </div>
                </motion.div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}